"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { UserRow } from "./user-table";
import { CustomerService, Customer, CustomerStatusCode } from "../../customer/services/customer-service";
import UserService from "../services/user-service";
import { OrderService } from "../../order-list/services/order-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

interface UserAssignCustomerModalProps {
  user: UserRow | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UserAssignCustomerModal: React.FC<UserAssignCustomerModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [searchAvailable, setSearchAvailable] = useState("");
  const [searchAssigned, setSearchAssigned] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { fetch: fetchCustomers } = CustomerService.list();
  const { fetch: assignCustomersApi } = UserService.assignCustomers();
  const { fetch: fetchCustomerDetail } = CustomerService.detail();
  const { fetch: resyncResponsibleApi } = OrderService.resyncResponsibleCustomerStaff();

  const fetchCustomersRef = useRef(fetchCustomers);
  const assignCustomersApiRef = useRef(assignCustomersApi);
  const fetchCustomerDetailRef = useRef(fetchCustomerDetail);
  const resyncResponsibleApiRef = useRef(resyncResponsibleApi);

  useEffect(() => {
    fetchCustomersRef.current = fetchCustomers;
    assignCustomersApiRef.current = assignCustomersApi;
    fetchCustomerDetailRef.current = fetchCustomerDetail;
    resyncResponsibleApiRef.current = resyncResponsibleApi;
  }, [fetchCustomers, assignCustomersApi, fetchCustomerDetail, resyncResponsibleApi]);

  // Parse customer IDs string into number array
  const parseCustomerIds = useCallback((customerIdsStr?: string): number[] => {
    if (!customerIdsStr) return [];
    try {
      if (customerIdsStr.startsWith("[")) {
        return JSON.parse(customerIdsStr).map(Number).filter((n: number) => !isNaN(n));
      }
      return customerIdsStr.split(",").map((s) => Number(s.trim())).filter((n) => !isNaN(n) && n > 0);
    } catch {
      return [];
    }
  }, []);

  // Fetch active customers from API
  const loadCustomers = useCallback(
    async (search = "") => {
      setIsLoadingCustomers(true);
      try {
        const res = await fetchCustomersRef.current({
          status: CustomerStatusCode.ACTIVE, // Chỉ lấy khách hàng còn Hoạt động
          key_search: search,
          limit: 100,
          page: 1,
        });
        if (res && isSuccess(res)) {
          const list = res.data?.list ?? [];
          setCustomerOptions(list);
          return list;
        }
      } catch (err) {
        console.error("Lỗi tải danh sách khách hàng", err);
      } finally {
        setIsLoadingCustomers(false);
      }
      return [];
    },
    []
  );

  // Initial load when modal opens
  useEffect(() => {
    if (!isOpen || !user) return;

    setSearchAvailable("");
    setSearchAssigned("");
    setCustomerOptions([]); // Clear available customers on open

    const initialIds = parseCustomerIds(user.customer_ids);

    void (async () => {
      if (initialIds.length > 0) {
        setIsLoadingCustomers(true);
        try {
          // Fetch details for all currently assigned customers so their names display correctly
          const detailsPromises = initialIds.map((id) => fetchCustomerDetailRef.current(id));
          const results = await Promise.all(detailsPromises);
          
          const loadedCustomers: Customer[] = [];
          results.forEach((res, idx) => {
            if (res && isSuccess(res) && res.data) {
              loadedCustomers.push(res.data);
            } else {
              // Fallback if failed to fetch
              const id = initialIds[idx];
              loadedCustomers.push({
                id,
                company_id: 2000,
                lead_id: "",
                total_spent: 0,
                completed_orders: 0,
                address: "",
                phone: "",
                name: `Khách hàng #${id}`,
                status: CustomerStatusCode.ACTIVE,
                created_at: "",
                updated_at: "",
                type_customer: 1,
                discount_percent: 0,
                default_payment_method: 0,
              });
            }
          });
          setSelectedCustomers(loadedCustomers);
        } catch (err) {
          console.error("Lỗi lấy thông tin khách hàng đã gán", err);
        } finally {
          setIsLoadingCustomers(false);
        }
      } else {
        setSelectedCustomers([]);
      }
    })();
  }, [isOpen, user?.id, user?.customer_ids, parseCustomerIds]);

  // Debounced search for available customers
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (searchAvailable.trim() === "") {
        setCustomerOptions([]);
      } else {
        void loadCustomers(searchAvailable);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchAvailable, isOpen, loadCustomers]);

  if (!isOpen || !user) return null;

  const handleAddCustomer = (customer: Customer) => {
    if (!selectedCustomers.some((c) => c.id === customer.id)) {
      setSelectedCustomers((prev) => [...prev, customer]);
    }
  };

  const handleRemoveCustomer = (id: number) => {
    setSelectedCustomers((prev) => prev.filter((c) => c.id !== id));
  };

  const handleAddAllVisible = () => {
    const unselected = unselectedAvailable.filter((c) => !selectedCustomers.some((s) => s.id === c.id));
    setSelectedCustomers((prev) => [...prev, ...unselected]);
  };

  const handleRemoveAllAssigned = () => {
    setSelectedCustomers([]);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const newIds = selectedCustomers.map((c) => c.id);
      const idsStr = newIds.join(",");
      const res = await assignCustomersApiRef.current(user.id, idsStr);
      if (res && isSuccess(res)) {
        // Đồng bộ snapshot NV phụ trách trên các đơn ĐANG MỞ của những khách bị ảnh hưởng
        // (khách vừa gán + khách vừa bỏ gán). Best-effort — lỗi ở đây không chặn việc gán.
        const affectedIds = [...new Set([...parseCustomerIds(user.customer_ids), ...newIds])];
        if (affectedIds.length > 0) {
          try {
            const r = await resyncResponsibleApiRef.current(affectedIds);
            if (!(r && isSuccess(r))) {
              showToast({ message: "Đã gán, nhưng chưa đồng bộ được đơn đang chạy. Vui lòng thử lại.", type: "warning" });
            }
          } catch {
            showToast({ message: "Đã gán, nhưng chưa đồng bộ được đơn đang chạy.", type: "warning" });
          }
        }
        showToast({ message: "Gán khách hàng thành công!", type: "success" });
        onSuccess();
        onClose();
      } else {
        showToast({ message: res?.message ?? "Không thể gán khách hàng", type: "error" });
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Đã xảy ra lỗi",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter available customers not yet selected
  const unselectedAvailable = customerOptions.filter(
    (opt) => !selectedCustomers.some((s) => s.id === opt.id)
  );

  // Filter assigned customers by search string
  const filteredAssigned = selectedCustomers.filter((c) => {
    if (!searchAssigned.trim()) return true;
    const q = searchAssigned.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.phone && c.phone.includes(q)) ||
      (c.address && c.address.toLowerCase().includes(q))
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 sm:p-6">
      <div className="w-full max-w-5xl rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col h-[85vh] max-h-[750px] border border-gray-100">
        
        {/* HEADER BANNER */}
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-5 bg-gradient-to-r from-purple-50 via-white to-gray-50">
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-purple-600 text-white shadow-md shadow-purple-200">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Gán Khách Hàng Cho Nhân Viên</h2>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 font-medium">
                <span>Nhân viên: <strong className="text-purple-700 font-bold">{user.name}</strong> ({user.username})</span>
                {user.department_name && (
                  <span className="rounded-md bg-purple-100/70 px-2.5 py-0.5 font-bold text-purple-700">
                    {user.department_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-200/70 hover:text-gray-700 transition cursor-pointer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 2-COLUMN BODY CONTENT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 sm:p-8 flex-1 overflow-hidden bg-gray-50/50">
          
          {/* LEFT PANEL: AVAILABLE ACTIVE CUSTOMERS */}
          <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-xs overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                  Khách Hàng Hoạt Động
                </h3>
                <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-extrabold text-green-700">
                  {unselectedAvailable.length}
                </span>
              </div>

              {unselectedAvailable.length > 0 && (
                <button
                  type="button"
                  onClick={handleAddAllVisible}
                  className="text-xs font-bold text-purple-600 hover:text-purple-800 hover:underline transition cursor-pointer"
                >
                  + Gán tất cả trang này
                </button>
              )}
            </div>

            {/* Search Available */}
            <div className="relative mb-3">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchAvailable}
                onChange={(e) => setSearchAvailable(e.target.value)}
                placeholder="Tìm tên, SĐT khách hàng..."
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 pl-9 pr-4 py-2 text-xs font-medium text-gray-800 placeholder:text-gray-400 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-purple-500/10 transition"
              />
            </div>

            {/* Available List Scrollable */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-2">
              {isLoadingCustomers ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 space-y-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"></div>
                  <span className="text-xs font-medium">Đang tải danh sách khách hàng...</span>
                </div>
              ) : unselectedAvailable.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center px-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 mb-2 opacity-50">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <p className="text-xs font-medium">
                    {!searchAvailable.trim() 
                      ? "Nhập từ khóa để tìm kiếm khách hàng" 
                      : "Không tìm thấy khách hàng khớp từ khóa"}
                  </p>
                </div>
              ) : (
                unselectedAvailable.map((c) => (
                  <div
                    key={c.id}
                    className="group flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50/50 p-3 hover:border-purple-200 hover:bg-purple-50/40 transition"
                  >
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-xs truncate">{c.name}</span>
                        <span className="rounded bg-green-100 px-1.5 py-0.2 text-[10px] font-bold text-green-700 shrink-0">
                          Hoạt động
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                        {c.phone ? <span className="font-semibold text-gray-700">{c.phone}</span> : "Chưa có SĐT"}
                        {c.address ? ` • ${c.address}` : ""}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleAddCustomer(c)}
                      className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-purple-700 transition cursor-pointer shrink-0"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                        <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                      </svg>
                      <span>Gán</span>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* RIGHT PANEL: ASSIGNED CUSTOMERS */}
          <div className="flex flex-col rounded-2xl border border-purple-200 bg-white p-5 shadow-xs overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-purple-900 uppercase tracking-wide">
                  Khách Hàng Đã Chọn Gán
                </h3>
                <span className="rounded-full bg-purple-600 px-2.5 py-0.5 text-xs font-extrabold text-white">
                  {selectedCustomers.length}
                </span>
              </div>

              {selectedCustomers.length > 0 && (
                <button
                  type="button"
                  onClick={handleRemoveAllAssigned}
                  className="text-xs font-bold text-red-600 hover:text-red-800 hover:underline transition cursor-pointer"
                >
                  Bỏ chọn tất cả
                </button>
              )}
            </div>

            {/* Search Assigned */}
            <div className="relative mb-3">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchAssigned}
                onChange={(e) => setSearchAssigned(e.target.value)}
                placeholder="Lọc danh sách đã chọn..."
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 pl-9 pr-4 py-2 text-xs font-medium text-gray-800 placeholder:text-gray-400 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-purple-500/10 transition"
              />
            </div>

            {/* Assigned List Scrollable */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-2">
              {selectedCustomers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-center px-6">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-purple-50 text-purple-400 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
                    </svg>
                  </div>
                  <p className="text-xs font-semibold text-gray-600">Chưa gán khách hàng nào</p>
                  <p className="text-[11px] text-gray-400 mt-1">Chọn từ danh sách bên trái để gán khách hàng cho nhân viên này.</p>
                </div>
              ) : filteredAssigned.length === 0 ? (
                <div className="py-8 text-center text-xs text-gray-400">
                  Không tìm thấy trong danh sách đã chọn
                </div>
              ) : (
                filteredAssigned.map((c) => (
                  <div
                    key={c.id}
                    className="group flex items-center justify-between rounded-xl border border-purple-100 bg-purple-50/30 p-3 hover:bg-red-50/40 hover:border-red-200 transition"
                  >
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="font-bold text-purple-950 text-xs truncate">{c.name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                        {c.phone ? <span className="font-semibold text-gray-700">{c.phone}</span> : "Chưa có SĐT"}
                        {c.address ? ` • ${c.address}` : ""}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveCustomer(c.id)}
                      className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-600 hover:text-white transition cursor-pointer shrink-0"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      <span>Bỏ gán</span>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-between border-t border-gray-100 px-8 py-4 bg-gray-50">
          <div className="text-xs text-gray-500 font-semibold">
            Đã chọn gán: <span className="text-purple-700 font-extrabold text-sm">{selectedCustomers.length}</span> khách hàng
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl border border-gray-300 bg-white px-6 py-2.5 text-xs font-bold text-gray-700 hover:bg-gray-100 transition cursor-pointer disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-7 py-2.5 text-xs font-bold text-white shadow-md shadow-purple-200 hover:bg-purple-700 transition cursor-pointer disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>Đang lưu...</span>
                </>
              ) : (
                <span>LƯU THAY ĐỔI</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAssignCustomerModal;
